import moment from 'moment/moment';
import { LoanCurrency } from '../../../types/loan/LoanCurrency';
import {
    FilterBooleanGroup,
    FilterDateGroup,
    FilterDateTimeGroup,
    FilterRadioGroup,
    FilterSelectGroup,
    FilterType,
} from '../../../types/filters/FilterGroup';
import { Rating, Ratings } from '../../../types/enums/Rating';
import { DataroomProvider, dataroomProviderTitles } from '../../../types/enums/loan/DataroomProvider';
import { LoanStatus } from '../../../types/loan/LoanStatus';
import { LoanReviewStatus, loanReviewStatusTitles } from '../../../types/loan/LoanReviewStatus';
import { dateRangeFilterOptions, dateTimeRangeFilterOptions } from '../../../constants/date-range.filter';
import { coupon } from '../../../constants/amr-pipeline/coupon';
import { FloaterIndex } from '../../../types/amr-pipeline/enums/FloaterIndex';
import { SubscriptionFeature } from '../../../types/billing/SubscriptionFeature';
import { user } from '../../../user';
import { roles } from '../../../constants';

export const getLoanMonitorFilters = () => {
    const isCollateralManager = user.hasRoles(roles.CollateralManager);

    function defaultFilter() {
        const canSeeDraft = user.hasRoles(roles.Administrator, roles.DataEntry);
        const hasLoanFullDetails = user.hasFeatures(SubscriptionFeature.LoanFullDetails);

        return {
            statuses: {
                key: 'statuses',
                type: FilterType.Select,
                visible: true,
                text: 'Status',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: Object.values(LoanStatus)
                    .filter(p => canSeeDraft || p !== LoanStatus.Draft)
                    .map(p => ({
                        value: p,
                        text: p.toString(),
                        selected: hasLoanFullDetails ? false : p === LoanStatus.Active,
                        visible: true,
                        disabled: false,
                    })),
                requiredFeature: SubscriptionFeature.LoanFullDetails,
            } as FilterSelectGroup<LoanStatus>,
            review: {
                key: 'review',
                type: FilterType.Select,
                visible: true,
                text: 'Review',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: Object.values(LoanReviewStatus).map(v => ({
                    value: v,
                    text: loanReviewStatusTitles[v],
                    selected: false,
                    visible: true,
                    disabled: false,
                })),
            } as FilterSelectGroup<LoanReviewStatus>,
            analyst: {
                key: 'analyst',
                withSearch: true,
                type: FilterType.Select,
                visible: true,
                text: 'Analyst',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: [],
            } as FilterSelectGroup<string>,
            currency: {
                key: 'currency',
                type: FilterType.Select,
                visible: true,
                text: 'Ccy',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: Object.values(LoanCurrency).map(v => ({
                    value: v,
                    text: v,
                    selected: false,
                    disabled: false,
                    visible: true,
                })),
            } as FilterSelectGroup<LoanCurrency>,
            arranger: {
                key: 'arranger',
                withSearch: true,
                type: FilterType.Select,
                visible: true,
                text: 'Arranger',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: [],
            } as FilterSelectGroup<string>,
            pricing: {
                key: 'pricing',
                type: FilterType.Date,
                visible: false,
                text: 'Pricing',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: {
                    selectedOption: undefined,
                    options: { customDateRange: { from: null, to: null } },
                },
                acceptableOptions: dateRangeFilterOptions.closing(),
                disabledDays: { after: moment().subtract(1, 'day').toDate() },
            } as FilterDateGroup,
            coArranger: {
                key: 'coArranger',
                withSearch: true,
                type: FilterType.Select,
                visible: false,
                text: 'Co-Arranger',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: [],
            } as FilterSelectGroup<string>,
            covLite: {
                key: 'covLite',
                type: FilterType.BooleanRadio,
                visible: false,
                text: 'Cov-Lite',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: { selectedOption: null },
            } as FilterBooleanGroup,
            dataroom: {
                key: 'dataroom',
                type: FilterType.Select,
                visible: false,
                text: 'Dataroom',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: Object.values(DataroomProvider).map(v => ({
                    value: v,
                    text: dataroomProviderTitles[v],
                    selected: false,
                    disabled: false,
                    visible: true,
                })),
            } as FilterSelectGroup<DataroomProvider>,
            due: {
                key: 'due',
                type: FilterType.DateWithYearsAndTimeRange,
                visible: false,
                text: 'Due',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: { selectedOption: undefined },
                acceptableOptions: dateTimeRangeFilterOptions.rollerDeadline(),
            } as FilterDateTimeGroup,
            industry: {
                key: 'industry',
                withSearch: true,
                type: FilterType.Select,
                visible: true,
                text: 'Industry',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: [],
            } as FilterSelectGroup<string>,
            useOfProceeds: {
                key: 'useOfProceeds',
                type: FilterType.Select,
                visible: false,
                text: 'Use of Proceeds',
                disabled: false,
                selected: false,
                isApplied: true,
                withSearch: true,
                filter: [],
            } as FilterSelectGroup<string>,
            maturity: {
                key: 'maturity',
                type: FilterType.Date,
                visible: false,
                text: 'Maturity',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: {
                    selectedOption: undefined,
                    options: { customDateRange: { from: null, to: null } },
                },
                acceptableOptions: dateRangeFilterOptions.maturity(),
                disabledDays: { before: moment().add(1, 'day').toDate() },
            } as FilterDateGroup,
            refIndex: {
                key: 'refIndex',
                type: FilterType.Radio,
                visible: false,
                text: 'Ref Index',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: {
                    selectedOption: null,
                    options: coupon.map(c => ({
                        value: c,
                        text: c,
                        selected: false,
                        disabled: false,
                        visible: true,
                    })),
                },
            } as FilterRadioGroup<FloaterIndex>,
            term: {
                key: 'term',
                type: FilterType.DateWithYearsRange,
                visible: false,
                text: 'Term',
                disabled: false,
                selected: false,
                isApplied: true,
                filter: {
                    selectedOption: undefined,
                    options: {
                        customYearsRange: { from: '', to: '' },
                        customDateRange: { from: null, to: null },
                    },
                },
                acceptableOptions: dateRangeFilterOptions.term(),
            } as FilterDateGroup,
            cfRatings: {
                key: 'cfRatings',
                type: FilterType.Select,
                visible: false,
                text: 'CF Rtg, M/S/F',
                disabled: false,
                selected: false,
                isApplied: false,
                filter: Ratings.map(v => ({
                    value: v,
                    text: v,
                    selected: false,
                    disabled: false,
                    visible: true,
                })),
            } as FilterSelectGroup<Rating>,
            ratings: {
                key: 'ratings',
                type: FilterType.Select,
                visible: false,
                text: 'Tranche Rtg, M/S/F',
                disabled: false,
                selected: false,
                isApplied: false,
                filter: Ratings.map(v => ({
                    value: v,
                    text: v,
                    selected: false,
                    disabled: false,
                    visible: true,
                })),
            } as FilterSelectGroup<Rating>,
        };
    }

    return {
        visibleFilters: isCollateralManager
            ? ['statuses', 'review', 'analyst', 'currency', 'arranger', 'industry']
            : ['statuses', 'currency', 'arranger', 'industry'],
        additionalFilters: ['pricing', 'coArranger', 'covLite', 'dataroom', 'due', 'maturity', 'refIndex', 'term', 'ratings', 'cfRatings', 'useOfProceeds'],
        defaultFilter,
    };
};
